import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-location';
import { useCallback, useEffect, useState } from 'react';
import type { PropertyModules } from '../../../../../packages/entities/Property';
import PageContainer from '../../core/components/PageContainer';
import ErrorBox from '../../core/components/ErrorBox';
import SpinnerIcon from '../../main/component/icon/SpinnerIcon';
import { apiRawFetch } from '../../main/client';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import { PropertySettingsContext } from './PropertySettingsContext';

export interface Property {
    id: number;
    name: string;
    minNights: number;
    currency: string;
    country: string;
    weekStartDay: 0 | 1;
    taxPresetId: string | null;
    modules: PropertyModules;
    created: string;
    updated: string;
}

const TABS = [
    { label: 'General', path: 'general' },
    { label: 'Access', path: 'access' },
    { label: 'Sharing', path: 'sharing' },
    { label: 'Modules', path: 'modules' },
] as const;

const PropertySettingsLayout = () => {
    const propertyId = usePropertyId();
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.current.pathname;

    const [property, setProperty] = useState<Property | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchProperty = async () => {
            if (!propertyId) return;

            try {
                setLoading(true);
                const response = await apiRawFetch(`api/properties/${propertyId}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch property');
                }
                const data = await response.json();
                setProperty(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Unknown error');
            } finally {
                setLoading(false);
            }
        };

        fetchProperty();
    }, [propertyId]);

    const refreshProperty = useCallback(async () => {
        if (!propertyId) return;
        try {
            const response = await apiRawFetch(`api/properties/${propertyId}`);
            if (response.ok) {
                const data = await response.json();
                setProperty(data);
            }
        } catch {
            // Silently ignore refresh errors
        }
    }, [propertyId]);

    if (!propertyId) {
        return null;
    }

    if (loading) {
        return <SpinnerIcon />;
    }

    if (error) {
        return <ErrorBox error={new Error(error)} />;
    }

    if (!property) {
        return <ErrorBox error={new Error('Property not found')} />;
    }

    const currentTab = TABS.findIndex(tab =>
        pathname.endsWith(`/settings/${tab.path}`)
    );
    const tabValue = currentTab >= 0 ? currentTab : 0;

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        const tab = TABS[newValue];
        navigate({ to: `/${propertyId}/settings/${tab.path}` });
    };

    const handleBack = () => {
        navigate({ to: '/properties' });
    };

    return (
        <PageContainer>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 3,
                }}
            >
                <Button
                    onClick={handleBack}
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    sx={{
                        borderRadius: 0,
                        borderWidth: '2px',
                        borderColor: '#374151',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 2,
                        py: 0.75,
                        '&:hover': {
                            borderWidth: '2px',
                            borderColor: '#6366f1',
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                        },
                    }}
                >
                    Back
                </Button>
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        fontSize: '2rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}
                >
                    {property.name}
                </Typography>
            </Box>

            <Box
                sx={{
                    borderBottom: '3px solid #6366f1',
                    backgroundColor: '#0f0f23',
                    mb: 3,
                }}
            >
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        '& .MuiTab-root': {
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontSize: '0.875rem',
                            minWidth: 'auto',
                            px: 3,
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#6366f1',
                            height: 3,
                        },
                    }}
                >
                    {TABS.map(tab => (
                        <Tab key={tab.path} label={tab.label} />
                    ))}
                </Tabs>
            </Box>

            <PropertySettingsContext.Provider value={{ property, refreshProperty }}>
                <Box>
                    <Outlet />
                </Box>
            </PropertySettingsContext.Provider>
        </PageContainer>
    );
};

export default PropertySettingsLayout;
